import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { supabase } from '../../lib/supabaseClient';

const AddClientModal = ({ open, onOpenChange, onSuccess, editMode = false, existingClient = null }) => {
  const [loading, setLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    abbreviation: '',
    abn: '',
    primary_procurement_email: '',
    email_preference: 'Draft',
    is_rcti_client: false,
    status: 'Active',
    notes: '',
  });

  // Client contacts state
  const [clientContacts, setClientContacts] = useState([]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Prefill form data when in edit mode
  useEffect(() => {
    if (editMode && existingClient && open) {
      setFormData({
        name: existingClient.name || '',
        abbreviation: existingClient.abbreviation || '',
        abn: existingClient.abn || '',
        primary_procurement_email: existingClient.primary_procurement_email || '',
        email_preference: existingClient.email_preference || 'Draft',
        is_rcti_client: existingClient.is_rcti_client || false,
        status: existingClient.status || 'Active',
        notes: existingClient.notes || '',
      });

      // Load existing contacts
      loadClientContacts(existingClient.id);
    }
  }, [editMode, existingClient, open]);

  const loadClientContacts = async (clientId) => {
    try {
      const { data, error } = await supabase
        .from('client_contacts')
        .select('*')
        .eq('client_id', clientId)
        .order('priority', { ascending: false });

      if (error) {
        // If table doesn't exist yet, just set empty array
        console.log('Client contacts table not available yet');
        setClientContacts([]);
        return;
      }
      setClientContacts(data || []);
    } catch (err) {
      console.error('Error loading client contacts:', err);
      // Set empty array on error
      setClientContacts([]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      abbreviation: '',
      abn: '',
      primary_procurement_email: '',
      email_preference: 'Draft',
      is_rcti_client: false,
      status: 'Active',
      notes: '',
    });
    setClientContacts([]);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Contact management functions
  const handleAddContact = () => {
    setClientContacts((prev) => [
      ...prev,
      {
        name: '',
        position: '',
        branch: '',
        division: '',
        email: '',
        phone: '',
        priority: 0,
      },
    ]);
  };

  const handleRemoveContact = (index) => {
    setClientContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleContactChange = (index, field, value) => {
    setClientContacts((prev) =>
      prev.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      )
    );
  };

  const validateABN = (abn) => {
    // Remove spaces and validate length
    const cleanABN = abn.replace(/\s/g, '');
    if (cleanABN.length === 0) return true; // ABN is optional
    if (cleanABN.length !== 11) return false;
    if (!/^\d+$/.test(cleanABN)) return false;
    return true;
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === '') return true; // Email is optional
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name || !formData.name.trim()) {
        throw new Error('Client name is required');
      }

      // Validate ABN if provided
      if (formData.abn && !validateABN(formData.abn)) {
        throw new Error('ABN must be 11 digits');
      }

      // Validate primary procurement email if provided
      if (formData.primary_procurement_email && !validateEmail(formData.primary_procurement_email)) {
        throw new Error('Invalid primary procurement email format');
      }

      // Validate contact emails
      for (let i = 0; i < clientContacts.length; i++) {
        const contact = clientContacts[i];
        if (contact.email && !validateEmail(contact.email)) {
          throw new Error(`Invalid email format for contact #${i + 1}`);
        }
        if (!contact.name || !contact.name.trim()) {
          throw new Error(`Contact #${i + 1} must have a name`);
        }
      }

      // Clean ABN (remove spaces)
      const dataToSubmit = {
        ...formData,
        abn: formData.abn ? formData.abn.replace(/\s/g, '') : null,
      };

      let clientId;

      if (editMode && existingClient) {
        // Update existing client
        const { error: updateError } = await supabase
          .from('clients')
          .update(dataToSubmit)
          .eq('id', existingClient.id);

        if (updateError) throw updateError;
        clientId = existingClient.id;

        // Delete existing contacts before reinserting
        const { error: deleteContactsError } = await supabase
          .from('client_contacts')
          .delete()
          .eq('client_id', clientId);

        if (deleteContactsError) throw deleteContactsError;
      } else {
        // Create new client
        const { data: newClient, error: clientError } = await supabase
          .from('clients')
          .insert([dataToSubmit])
          .select()
          .single();

        if (clientError) throw clientError;
        clientId = newClient.id;
      }

      // Insert contacts if any
      if (clientContacts.length > 0) {
        const contactsToInsert = clientContacts.map((contact) => ({
          client_id: clientId,
          name: contact.name,
          position: contact.position || null,
          branch: contact.branch || null,
          division: contact.division || null,
          email: contact.email || null,
          phone: contact.phone || null,
          priority: parseInt(contact.priority) || 0,
        }));

        const { error: contactsError } = await supabase
          .from('client_contacts')
          .insert(contactsToInsert);

        if (contactsError) throw contactsError;
      }

      onSuccess();
      onOpenChange(false);
    } catch (err) {
      console.error('Error saving client:', err);
      alert('Error saving client: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
        <form onSubmit={handleSubmit} className="contents">
          {/* Header - sticky at top */}
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Client' : 'Add New Client'}</DialogTitle>
              <DialogDescription>
                {editMode ? 'Update client information' : 'Enter client details to add to the system'}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6">
          {/* Basic Information */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-lg">Client Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Client Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Australian Federal Police"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="abbreviation">Abbreviation</Label>
                  <Input
                    id="abbreviation"
                    name="abbreviation"
                    value={formData.abbreviation}
                    onChange={handleInputChange}
                    placeholder="e.g., AFP"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="abn">ABN (Australian Business Number)</Label>
                  <Input
                    id="abn"
                    name="abn"
                    value={formData.abn}
                    onChange={handleInputChange}
                    placeholder="e.g., 12 345 678 901 (11 digits)"
                    maxLength={14}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter 11 digits (spaces optional)
                  </p>
                </div>

                <div>
                  <Label htmlFor="primary_procurement_email">Primary Procurement Email</Label>
                  <Input
                    id="primary_procurement_email"
                    name="primary_procurement_email"
                    type="email"
                    value={formData.primary_procurement_email}
                    onChange={handleInputChange}
                    placeholder="e.g., procurement@afp.gov.au"
                  />
                </div>

                <div>
                  <Label htmlFor="email_preference">Email Preference</Label>
                  <select
                    id="email_preference"
                    name="email_preference"
                    value={formData.email_preference}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="Draft">Draft - Create email drafts only</option>
                    <option value="Send">Send - Automatically send emails (future)</option>
                    <option value="No_email">No Email - Don't send any emails</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Controls automated RFQ email behavior
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="Active">Active</option>
                    <option value="Pending">Pending</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pending = Auto-created from RFQ import
                  </p>
                </div>
                <div className="flex items-center">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_rcti_client"
                      name="is_rcti_client"
                      checked={formData.is_rcti_client}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="is_rcti_client" className="cursor-pointer">
                      RCTI Client
                    </Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Client Contacts */}
          <Card className="mb-4">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Client Contacts</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={handleAddContact}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Contact
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {clientContacts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No contacts added. Click "Add Contact" to add contact information.
                </p>
              ) : (
                <div className="space-y-4">
                  {clientContacts.map((contact, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <h4 className="font-medium text-sm">Contact {index + 1}</h4>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveContact(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor={`contact-name-${index}`}>Name *</Label>
                          <Input
                            id={`contact-name-${index}`}
                            value={contact.name}
                            onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                            placeholder="Contact name"
                          />
                        </div>

                        <div>
                          <Label htmlFor={`contact-position-${index}`}>Position</Label>
                          <Input
                            id={`contact-position-${index}`}
                            value={contact.position}
                            onChange={(e) => handleContactChange(index, 'position', e.target.value)}
                            placeholder="e.g., Procurement Manager"
                          />
                        </div>

                        <div>
                          <Label htmlFor={`contact-branch-${index}`}>Branch</Label>
                          <Input
                            id={`contact-branch-${index}`}
                            value={contact.branch}
                            onChange={(e) => handleContactChange(index, 'branch', e.target.value)}
                            placeholder="Branch name"
                          />
                        </div>

                        <div>
                          <Label htmlFor={`contact-division-${index}`}>Division</Label>
                          <Input
                            id={`contact-division-${index}`}
                            value={contact.division}
                            onChange={(e) => handleContactChange(index, 'division', e.target.value)}
                            placeholder="Division name"
                          />
                        </div>

                        <div>
                          <Label htmlFor={`contact-email-${index}`}>Email</Label>
                          <Input
                            id={`contact-email-${index}`}
                            type="email"
                            value={contact.email}
                            onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                            placeholder="contact@example.com"
                          />
                        </div>

                        <div>
                          <Label htmlFor={`contact-phone-${index}`}>Phone</Label>
                          <Input
                            id={`contact-phone-${index}`}
                            value={contact.phone}
                            onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                            placeholder="+61 400 123 456"
                          />
                        </div>

                        <div>
                          <Label htmlFor={`contact-priority-${index}`}>Priority</Label>
                          <Input
                            id={`contact-priority-${index}`}
                            type="number"
                            value={contact.priority}
                            onChange={(e) => handleContactChange(index, 'priority', e.target.value)}
                            placeholder="0"
                            min="0"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Higher number = higher priority for email search
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-lg">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                placeholder="Internal notes about the client..."
              />
            </CardContent>
          </Card>
          </div>

          {/* Footer - sticky at bottom */}
          <div className="border-t p-6 bg-background">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {editMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>{editMode ? 'Update Client' : 'Create Client'}</>
                )}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddClientModal;
